'use client'

import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { PhoneInput } from '@/components/ui/phone-input'
import { Switch } from '@/components/ui/switch'
import { Turnstile } from '@marsidev/react-turnstile'
import { Award, Coins, Loader2, LucideIcon, Percent, Tag } from 'lucide-react'
import React, { useState } from 'react'
import { useRouter } from 'next/navigation'
import {
  completeRegistration,
  requestIdentityOtp,
  requestLoginOtp,
  verifyIdentityOtp,
  verifyLoginOtp,
} from './action'
import { redirect } from 'next/navigation'
import { Label } from '@/components/ui/label'
import { AccountTranslation } from '@/payload-types'
import Link from 'next/link'
import { useTranslations } from 'next-intl'
import { formatDisplayPhone } from '@/utilities/formatProfile'

type Step =
  | { flow: 'login'; mode: 'input'; inputType: 'Phone' | 'Email' }
  | { flow: 'login'; mode: 'otp'; inputType: 'Phone' | 'Email'; otpId: string }
  | { flow: 'add-phone'; mode: 'input' }
  | { flow: 'add-phone'; mode: 'otp'; otpId: string }
  | { flow: 'add-email'; mode: 'input' }
  | { flow: 'add-email'; mode: 'otp'; otpId: string }
  | { flow: 'consent'; mode: 'input' }

const iconMap: Record<string, LucideIcon> = {
  tag: Tag,
  award: Award,
  coins: Coins,
  percent: Percent,
}

type LoginTranslations = AccountTranslation['loginPage']

export function LoginClient({
  translations,
  initialEmail,
}: {
  translations: LoginTranslations
  initialEmail?: string
}) {
  const t = translations
  const tGlobal = useTranslations()
  const router = useRouter()

  const [step, setStep] = useState<Step>({ flow: 'login', mode: 'input', inputType: 'Email' })
  const [phoneNumber, setPhoneNumber] = useState('')
  const [email, setEmail] = useState(initialEmail ?? '')
  const [verificationCode, setVerificationCode] = useState('')
  const [turnstileToken, setTurnstileToken] = useState('')
  const [turnstileKey, setTurnstileKey] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [isSending, setIsSending] = useState(false)
  const [isVerifying, setIsVerifying] = useState(false)
  const [hasConsented, setHasConsented] = useState(false)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')

  const identifier =
    step.flow === 'add-phone'
      ? phoneNumber
      : step.flow === 'add-email'
        ? email
        : step.flow === 'login' && step.inputType === 'Phone'
          ? phoneNumber
          : email

  const resetTurnstile = () => {
    setTurnstileToken('')
    setTurnstileKey((k) => k + 1)
  }

  const handleSend = async () => {
    setError(null)
    setIsSending(true)
    try {
      if (step.flow === 'login') {
        const result = await requestLoginOtp(identifier, step.inputType, turnstileToken)
        if ('error' in result) {
          setError(result.error)
          resetTurnstile()
          return
        }
        setStep({ flow: 'login', mode: 'otp', inputType: step.inputType, otpId: result.otpId })
      } else if (step.flow === 'add-phone') {
        const result = await requestIdentityOtp(phoneNumber, 'Phone', turnstileToken)
        if ('error' in result) {
          setError(result.error)
          resetTurnstile()
          return
        }
        setStep({ flow: 'add-phone', mode: 'otp', otpId: result.otpId })
      } else {
        const result = await requestIdentityOtp(email, 'Email', turnstileToken)
        if ('error' in result) {
          setError(result.error)
          resetTurnstile()
          return
        }
        setStep({ flow: 'add-email', mode: 'otp', otpId: result.otpId })
      }
    } finally {
      setIsSending(false)
    }
  }

  const handleVerify = async () => {
    setError(null)
    setIsVerifying(true)
    try {
      if (step.flow === 'login' && step.mode === 'otp') {
        const result = await verifyLoginOtp(
          identifier,
          step.inputType,
          step.otpId,
          verificationCode,
        )
        if ('error' in result) {
          setError(result.error)
          return
        }
        if (!result.hasPhonenumber) {
          setPhoneNumber('')
          setVerificationCode('')
          resetTurnstile()
          setStep({ flow: 'add-phone', mode: 'input' })
        } else if (!result.hasEmail) {
          setEmail('')
          setVerificationCode('')
          resetTurnstile()
          setStep({ flow: 'add-email', mode: 'input' })
        } else {
          redirect('/account')
        }
      } else if (step.flow === 'add-phone' && step.mode === 'otp') {
        const result = await verifyIdentityOtp(phoneNumber, 'Phone', step.otpId, verificationCode)
        if ('error' in result) {
          setError(result.error)
          return
        }
        setVerificationCode('')
        setStep({ flow: 'consent', mode: 'input' })
      } else if (step.flow === 'add-email' && step.mode === 'otp') {
        const result = await verifyIdentityOtp(email, 'Email', step.otpId, verificationCode)
        if ('error' in result) {
          setError(result.error)
          return
        }
        setVerificationCode('')
        setStep({ flow: 'consent', mode: 'input' })
      }
    } finally {
      setIsVerifying(false)
    }
  }

  const handleBack = () => {
    setVerificationCode('')
    resetTurnstile()
    if (step.flow === 'login') {
      setStep({ flow: 'login', mode: 'input', inputType: step.inputType })
    } else if (step.flow === 'add-phone') {
      setStep({ flow: 'add-phone', mode: 'input' })
    } else {
      setStep({ flow: 'add-email', mode: 'input' })
    }
  }

  const heading =
    step.flow === 'add-phone'
      ? t?.addPhoneHeading
      : step.flow === 'add-email'
        ? t?.addEmailHeading
        : step.flow === 'consent'
          ? tGlobal('consent_heading')
          : t?.welcomeHeading

  return (
    <div className='flex min-h-[75vh] items-center justify-center px-4 py-16'>
      <div className='grid w-full max-w-5xl grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16'>
        {/* Left: Login form */}
        <div className='flex flex-col gap-8 py-4'>
          <h1 className='text-[32px] leading-10 font-medium text-stone-950'>{heading}</h1>

          {step.flow === 'consent' ? null : step.mode === 'otp' ? (
            <form
              onSubmit={(e) => {
                e.preventDefault()
                if (isVerifying || !verificationCode) return
                handleVerify()
              }}
              className='flex flex-col gap-4'
            >
              <p className='text-base text-stone-500'>
                {t?.enterCodeSentTo}{' '}
                <span className='font-medium text-stone-950'>{formatDisplayPhone(identifier)}</span>
              </p>
              <div className='space-y-2'>
                <Label>{t?.verificationCodeLabel}</Label>
                <Input
                  placeholder='* * * * *'
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  className='h-[54px] rounded-lg border-stone-200 bg-white text-center text-base'
                />
              </div>
              <Button
                type='submit'
                size='lg'
                className='h-14 w-full rounded-full'
                disabled={isVerifying || !verificationCode}
              >
                {isVerifying ? <Loader2 className='size-4 animate-spin' /> : t?.verifyButton}
              </Button>
              {error && <p className='text-sm text-red-500'>{error}</p>}
              <button
                type='button'
                onClick={handleBack}
                disabled={isVerifying}
                className='text-center text-sm text-stone-500 underline underline-offset-4 hover:text-stone-700 disabled:pointer-events-none disabled:opacity-50'
              >
                {t?.backButton}
              </button>
            </form>
          ) : (
            <>
              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  if (isSending || !turnstileToken || !(phoneNumber || email)) return
                  handleSend()
                }}
                className='flex flex-col gap-6'
              >
                {step.flow === 'add-phone' && <div>{t?.addPhoneDescription}</div>}
                {step.flow === 'add-email' && <div>{t?.addEmailDescription}</div>}
                {step.flow === 'add-phone' ||
                (step.flow === 'login' && step.inputType === 'Phone') ? (
                  <div className='space-y-2'>
                    <Label>{tGlobal('phone')}</Label>
                    <PhoneInput
                      onChange={(value) => setPhoneNumber(value)}
                      value={phoneNumber}
                      className='h-[46px] rounded-lg border-stone-200 bg-white text-base'
                    />
                  </div>
                ) : (
                  <div className='space-y-2'>
                    <Label>{tGlobal('email')}</Label>
                    <Input
                      type='email'
                      placeholder='your@email.com'
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className='h-[46px] rounded-lg border-stone-200 bg-white text-base'
                    />
                  </div>
                )}

                <Button
                  type='submit'
                  size='lg'
                  className='h-14 w-full rounded-full'
                  disabled={isSending || !turnstileToken || !(phoneNumber || email)}
                >
                  {isSending ? <Loader2 className='size-4 animate-spin' /> : t?.sendCodeButton}
                </Button>
                {error && <p className='text-sm text-red-500'>{error}</p>}
                {step.flow === 'login' && (
                  <>
                    {/* <div className='flex items-center gap-4'>
                    <hr className='flex-1 border-stone-200' />
                    <span className='text-sm text-stone-500'>{t?.orDivider}</span>
                    <hr className='flex-1 border-stone-200' />
                  </div>
                  <Button
                    variant='outline'
                    size='lg'
                    className='h-14 w-full rounded-full border-[#dcceae]'
                  >
                    {t?.becomeMemberButton}
                  </Button> */}
                    <button
                      type='button'
                      onClick={() =>
                        setStep({
                          flow: 'login',
                          mode: 'input',
                          inputType: step.inputType === 'Phone' ? 'Email' : 'Phone',
                        })
                      }
                      disabled={isSending}
                      className='text-center text-sm text-stone-500 underline underline-offset-4 hover:text-stone-700 disabled:pointer-events-none disabled:opacity-50'
                    >
                      {step.inputType === 'Phone' ? t?.useEmailInstead : t?.usePhoneInstead}
                    </button>
                  </>
                )}
              </form>
              <div inert>
                <Turnstile
                  key={turnstileKey}
                  siteKey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY!}
                  options={{ appearance: 'interaction-only' }}
                  onSuccess={setTurnstileToken}
                />
              </div>
            </>
          )}

          {step.flow === 'consent' && (
            <div className='flex flex-col gap-4'>
              <div className='flex flex-col gap-2'>
                <Label>{tGlobal('name_label')}</Label>
                <div className='flex-1 space-y-2'>
                  <Input
                    placeholder={tGlobal('first_name')}
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className='h-[46px] rounded-lg border-stone-200 bg-white text-base'
                  />
                </div>
                <div className='flex-1 space-y-2'>
                  <Input
                    placeholder={tGlobal('last_name')}
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className='h-[46px] rounded-lg border-stone-200 bg-white text-base'
                  />
                </div>
              </div>
              <div>
                <div className='text-foreground pb-2 text-sm'>
                  {tGlobal('terms_before_link')}{' '}
                  <Link
                    href='/first-member/first-member-terms-conditions'
                    target='_blank'
                    className='text-muted-foreground hover:text-foreground underline transition-colors'
                  >
                    {tGlobal('read_terms')}
                  </Link>{' '}
                  {tGlobal('terms_after_link')}
                </div>
              </div>
              <Card>
                <CardContent className='flex items-center justify-between gap-4'>
                  <Label htmlFor='consent-switch' className='cursor-pointer text-base'>
                    {tGlobal('consent_accept_label')}
                  </Label>
                  <Switch
                    id='consent-switch'
                    checked={hasConsented}
                    onCheckedChange={setHasConsented}
                  />
                </CardContent>
              </Card>
              <Button
                onClick={async () => {
                  setError(null)
                  setIsSending(true)
                  const result = await completeRegistration(firstName, lastName, email)
                  if ('error' in result) {
                    setIsSending(false)
                    setError(result.error)
                  } else {
                    router.push('/account')
                  }
                }}
                size='lg'
                className='h-14 w-full rounded-full'
                disabled={!hasConsented || isSending || !firstName || !lastName}
              >
                {isSending ? <Loader2 className='size-4 animate-spin' /> : tGlobal('confirm_login')}
              </Button>
              {error && <p className='text-sm text-red-500'>{error}</p>}
            </div>
          )}
        </div>

        {/* Right: Member benefits card */}
        <div className='flex flex-col gap-6 rounded-3xl bg-white p-12'>
          <div className='flex flex-col gap-2'>
            <h2 className='text-[32px] leading-10 font-medium text-stone-950'>
              {t?.memberBenefitsHeading}
            </h2>
            <p className='text-base text-stone-700'>{t?.memberBenefitsDescription}</p>
          </div>
          <div className='flex flex-col gap-2'>
            {t?.loginBenefits?.map((benefit) => {
              const Icon = iconMap[benefit.icon ?? '']
              return (
                <div key={benefit.id} className='flex items-center gap-2'>
                  <div className='shrink-0 rounded-full bg-[#f3eee2] p-1.5'>
                    {Icon && <Icon className='size-5 text-stone-700' />}
                  </div>
                  <span className='text-base text-stone-700'>{benefit.label}</span>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
